// src/pages/business/marketing/PromotionsPage.tsx
// Promotions & Marketing management page

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Tag, Percent, Gift, Loader2, Search,
  Calendar, Clock, CheckCircle, XCircle, Edit, Trash2,
  Copy, Share2, TrendingUp, Users, Package
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';

interface BulkTier {
  min_quantity: number;
  discount_percentage: number;
}

interface Promotion {
  id: string;
  code: string;
  type: 'percentage' | 'fixed' | 'buy_x_get_y' | 'bulk_discount';
  value: number;
  min_purchase?: number;
  max_uses?: number;
  used_count: number;
  starts_at: string;
  expires_at?: string;
  is_active: boolean;
  description?: string;
  created_at: string;
  bulk_tiers?: BulkTier[];
}

const promotionTypeConfig: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  percentage: { label: 'Percentage', icon: Percent, color: '#3B82F6' },
  fixed: { label: 'Fixed Amount', icon: Tag, color: '#10B981' },
  buy_x_get_y: { label: 'Buy X Get Y', icon: Gift, color: '#F59E0B' },
  bulk_discount: { label: 'Bulk Discount', icon: Package, color: '#8B5CF6' },
};

const PromotionsPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Form state for new promotion
  const [newCode, setNewCode] = useState('');
  const [newType, setNewType] = useState<'percentage' | 'fixed' | 'buy_x_get_y' | 'bulk_discount'>('percentage');
  const [newValue, setNewValue] = useState('');
  const [newMinPurchase, setNewMinPurchase] = useState('');
  const [newMaxUses, setNewMaxUses] = useState('');
  const [newStartsAt, setNewStartsAt] = useState('');
  const [newExpiresAt, setNewExpiresAt] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Bulk discount fields
  const [bulkTiers, setBulkTiers] = useState<BulkTier[]>([
    { min_quantity: 5, discount_percentage: 5 },
    { min_quantity: 10, discount_percentage: 10 },
    { min_quantity: 25, discount_percentage: 15 },
  ]);

  useEffect(() => {
    fetchPromotions();
  }, [businessId]);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/businesses/${businessId}/promotions`);
      setPromotions(res.data.promotions || []);
    } catch (error: any) {
      console.error('Failed to fetch promotions:', error);
      toastError('Failed to load promotions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    fetchPromotions();
  };

  const togglePromotionStatus = async (promotionId: string, currentStatus: boolean) => {
    try {
      await apiClient.patch(`/businesses/${businessId}/promotions/${promotionId}`, {
        is_active: !currentStatus,
      });
      fetchPromotions();
      toastSuccess(`Promotion ${currentStatus ? 'disabled' : 'enabled'}`);
    } catch (error: any) {
      toastError('Failed to update promotion');
    }
  };

  const deletePromotion = async (promotionId: string) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;
    try {
      await apiClient.delete(`/businesses/${businessId}/promotions/${promotionId}`);
      fetchPromotions();
      toastSuccess('Promotion deleted');
    } catch (error: any) {
      toastError('Failed to delete promotion');
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toastSuccess('Code copied to clipboard');
  };

  const handleCreatePromotion = async () => {
    if (!newCode.trim()) {
      toastError('Please enter a promotion code');
      return;
    }
    if (newType !== 'bulk_discount' && (!newValue || parseFloat(newValue) <= 0)) {
      toastError('Please enter a valid value');
      return;
    }
    if (newType === 'bulk_discount' && bulkTiers.length === 0) {
      toastError('Please add at least one bulk discount tier');
      return;
    }

    setCreating(true);
    try {
      await apiClient.post(`/businesses/${businessId}/promotions`, {
        code: newCode.trim().toUpperCase(),
        type: newType,
        value: newType === 'bulk_discount' ? 0 : parseFloat(newValue),
        min_purchase: newMinPurchase ? parseFloat(newMinPurchase) : undefined,
        max_uses: newMaxUses ? parseInt(newMaxUses) : undefined,
        starts_at: newStartsAt || new Date().toISOString(),
        expires_at: newExpiresAt || undefined,
        description: newDescription,
        bulk_tiers: newType === 'bulk_discount' ? bulkTiers : undefined,
      });
      toastSuccess('Promotion created successfully');
      closeCreateModal();
      fetchPromotions();
    } catch (error: any) {
      toastError(error.response?.data?.error || 'Failed to create promotion');
    } finally {
      setCreating(false);
    }
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewCode('');
    setNewType('percentage');
    setNewValue('');
    setNewMinPurchase('');
    setNewMaxUses('');
    setNewStartsAt('');
    setNewExpiresAt('');
    setNewDescription('');
    setBulkTiers([
      { min_quantity: 5, discount_percentage: 5 },
      { min_quantity: 10, discount_percentage: 10 },
      { min_quantity: 25, discount_percentage: 15 },
    ]);
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCode(code);
  };

  const filteredPromotions = promotions.filter((p) =>
    p.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const isExpired = (promotion: Promotion) => {
    if (!promotion.expires_at) return false;
    return new Date(promotion.expires_at) < new Date();
  };

  const isStarted = (promotion: Promotion) => {
    return new Date(promotion.starts_at) <= new Date();
  };

  const activeCount = promotions.filter((p) => p.is_active && !isExpired(p) && isStarted(p)).length;
  const totalUses = promotions.reduce((sum, p) => sum + p.used_count, 0);

  const renderPromotionValue = (promotion: Promotion) => {
    if (promotion.type === 'bulk_discount' && promotion.bulk_tiers) {
      return (
        <div className="space-y-1">
          {promotion.bulk_tiers.map((tier, idx) => (
            <div key={idx} className="text-sm">
              {tier.min_quantity}+ items: {tier.discount_percentage}% OFF
            </div>
          ))}
        </div>
      );
    }
    return (
      <p className="font-semibold text-sm text-light-text dark:text-dark-text">
        {promotion.type === 'percentage'
          ? `${promotion.value}% OFF`
          : promotion.type === 'fixed'
          ? `KES ${promotion.value} OFF`
          : `${promotion.value}`}
      </p>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
            Promotions
          </h1>
        </div>
        <div className="flex items-center justify-center py-24">
          <Loader2 size={40} className="animate-spin mx-auto text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/business/${businessId}`)} className="flex-shrink-0">
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="flex-1 font-bold text-light-text dark:text-dark-text" style={{ fontSize: 20 }}>
          Promotions & Discounts
        </h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold text-white"
          style={{ backgroundColor: '#4F46E5' }}
        >
          <Plus size={16} />
          New
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 pt-4">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Tag size={16} className="text-primary" />
              <p className="text-xs text-light-subtle dark:text-dark-subtle">Active</p>
            </div>
            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{activeCount}</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-green-500" />
              <p className="text-xs text-light-subtle dark:text-dark-subtle">Total Uses</p>
            </div>
            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{totalUses}</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Gift size={16} className="text-purple-500" />
              <p className="text-xs text-light-subtle dark:text-dark-subtle">Total</p>
            </div>
            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{promotions.length}</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
          <input
            type="text"
            placeholder="Search promotions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>

        {/* Promotions List */}
        {filteredPromotions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
            <Gift size={64} className="text-light-subtle dark:text-dark-subtle" />
            <p className="font-semibold text-light-text dark:text-dark-text" style={{ fontSize: 18 }}>
              No Promotions Found
            </p>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              {searchQuery ? 'Try adjusting your search' : 'Create your first promotion to boost sales'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-2 px-6 py-3 rounded-xl text-white font-semibold"
                style={{ backgroundColor: '#4F46E5' }}
              >
                Create Promotion
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPromotions.map((promotion) => {
              const typeConfig = promotionTypeConfig[promotion.type];
              const TypeIcon = typeConfig.icon;
              const expired = isExpired(promotion);
              const started = isStarted(promotion);
              const isActive = promotion.is_active && !expired && started;

              return (
                <div
                  key={promotion.id}
                  className="bg-light-card dark:bg-dark-card rounded-xl p-4 border border-light-border dark:border-dark-border shadow-sm"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${typeConfig.color}20` }}
                      >
                        <TypeIcon size={20} style={{ color: typeConfig.color }} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-bold text-light-text dark:text-dark-text font-mono">
                            {promotion.code}
                          </p>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-semibold ${
                              isActive
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                                : expired
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                                : !started
                                ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                                : 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {isActive ? 'Active' : expired ? 'Expired' : !started ? 'Scheduled' : 'Inactive'}
                          </span>
                        </div>
                        {promotion.description && (
                          <p className="text-xs text-light-subtle dark:text-dark-subtle mt-1">
                            {promotion.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => copyCode(promotion.code)}
                      className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                      title="Copy code"
                    >
                      <Copy size={16} className="text-light-subtle dark:text-dark-subtle" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Value</p>
                        {renderPromotionValue(promotion)}
                      </div>
                      <div>
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Uses</p>
                        <p className="font-semibold text-sm text-light-text dark:text-dark-text">
                          {promotion.used_count}{promotion.max_uses ? `/${promotion.max_uses}` : ''}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-light-subtle dark:text-dark-subtle">Valid</p>
                        <p className="font-semibold text-sm text-light-text dark:text-dark-text">
                          {formatDate(promotion.starts_at)} - {promotion.expires_at ? formatDate(promotion.expires_at) : 'No expiry'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => togglePromotionStatus(promotion.id, promotion.is_active)}
                        className={`p-2 rounded-lg transition-colors ${
                          promotion.is_active
                            ? 'text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20'
                            : 'text-gray-400 hover:bg-light-bg dark:hover:bg-dark-bg'
                        }`}
                        title={promotion.is_active ? 'Disable' : 'Enable'}
                      >
                        {promotion.is_active ? <CheckCircle size={18} /> : <XCircle size={18} />}
                      </button>
                      <button
                        onClick={() => deletePromotion(promotion.id)}
                        className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <button
        onClick={onRefresh}
        disabled={refreshing}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-50"
        style={{ backgroundColor: '#4F46E5' }}
      >
        <Loader2 size={24} className={`text-white ${refreshing ? 'animate-spin' : ''}`} />
      </button>

      {/* Create Promotion Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-light-card dark:bg-dark-card rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-light-text dark:text-dark-text">Create Promotion</h2>
              <button
                onClick={closeCreateModal}
                className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
              >
                <XCircle size={20} className="text-light-subtle dark:text-dark-subtle" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Code */}
              <div>
                <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                  Promotion Code <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                    placeholder="e.g., SUMMER2024"
                    className="flex-1 px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text font-mono focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <button
                    onClick={generateCode}
                    className="px-4 py-3 rounded-xl border border-light-border dark:border-dark-border text-sm font-semibold text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                  >
                    Generate
                  </button>
                </div>
              </div>

              {/* Type */}
              <div>
                <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                  Discount Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(promotionTypeConfig).map(([key, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setNewType(key as any)}
                        className={`p-3 rounded-xl border transition-all ${
                          newType === key
                            ? 'border-primary bg-primary/10 dark:bg-primary/20'
                            : 'border-light-border dark:border-dark-border hover:bg-light-bg dark:hover:bg-dark-bg'
                        }`}
                      >
                        <Icon size={20} className="mx-auto mb-1" style={{ color: newType === key ? config.color : undefined }} />
                        <p className={`text-xs font-medium ${newType === key ? 'text-primary' : 'text-light-subtle dark:text-dark-subtle'}`}>
                          {config.label}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Value - only show for non-bulk discounts */}
              {newType !== 'bulk_discount' && (
                <div>
                  <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                    Value <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      placeholder={newType === 'percentage' ? "e.g., 20" : "e.g., 100"}
                      className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-light-subtle dark:text-dark-subtle">
                      {newType === 'percentage' ? '%' : 'KES'}
                    </span>
                  </div>
                </div>
              )}

              {/* Bulk Discount Tiers */}
              {newType === 'bulk_discount' && (
                <div>
                  <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                    Discount Tiers <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-3">
                    {bulkTiers.map((tier, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <div className="flex-1">
                          <input
                            type="number"
                            value={tier.min_quantity}
                            onChange={(e) => {
                              const newTiers = [...bulkTiers];
                              newTiers[index].min_quantity = parseInt(e.target.value) || 0;
                              setBulkTiers(newTiers);
                            }}
                            placeholder="Min qty"
                            className="w-full px-3 py-2 rounded-lg bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                          />
                        </div>
                        <span className="text-light-subtle dark:text-dark-subtle text-sm">+</span>
                        <div className="flex-1">
                          <input
                            type="number"
                            value={tier.discount_percentage}
                            onChange={(e) => {
                              const newTiers = [...bulkTiers];
                              newTiers[index].discount_percentage = parseInt(e.target.value) || 0;
                              setBulkTiers(newTiers);
                            }}
                            placeholder="Discount %"
                            className="w-full px-3 py-2 rounded-lg bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                          />
                        </div>
                        <span className="text-light-subtle dark:text-dark-subtle text-sm">%</span>
                        {bulkTiers.length > 1 && (
                          <button
                            onClick={() => {
                              const newTiers = bulkTiers.filter((_, i) => i !== index);
                              setBulkTiers(newTiers);
                            }}
                            className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <XCircle size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      setBulkTiers([...bulkTiers, { min_quantity: 0, discount_percentage: 0 }]);
                    }}
                    className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
                  >
                    <Plus size={16} />
                    Add Tier
                  </button>
                </div>
              )}

              {/* Min Purchase & Max Uses */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                    Min Purchase (KES)
                  </label>
                  <input
                    type="number"
                    value={newMinPurchase}
                    onChange={(e) => setNewMinPurchase(e.target.value)}
                    placeholder="Optional"
                    className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                    Max Uses
                  </label>
                  <input
                    type="number"
                    value={newMaxUses}
                    onChange={(e) => setNewMaxUses(e.target.value)}
                    placeholder="Unlimited"
                    className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                    Starts At
                  </label>
                  <input
                    type="datetime-local"
                    value={newStartsAt}
                    onChange={(e) => setNewStartsAt(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                    Expires At
                  </label>
                  <input
                    type="datetime-local"
                    value={newExpiresAt}
                    onChange={(e) => setNewExpiresAt(e.target.value)}
                    placeholder="No expiry"
                    className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                  Description
                </label>
                <textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Optional description for this promotion..."
                  rows={2}
                  className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-light-card dark:bg-dark-card border-t border-light-border dark:border-dark-border px-6 py-4">
              <button
                onClick={handleCreatePromotion}
                disabled={creating}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-semibold transition-all hover:opacity-90 disabled:opacity-50"
                style={{ backgroundColor: '#4F46E5' }}
              >
                {creating ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Gift size={18} />
                    Create Promotion
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionsPage;